import { Formik, FormikProps } from 'formik';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { FieldCard, FormField, Skeleton } from '@components';
import {
  getIpBlacklistFormConfig,
  getSecurityFormConfig,
  getUserAgentBlacklistFormConfig,
  SCREEN_BREAKPOINTS,
  securityFormSchema,
  VALIDATION_LIMITS,
} from '@constants';
import { FormType, InputType, StatusType, Translation } from '@enums';
import {
  useAppDispatch,
  useCreateModal,
  useErrorFormInner,
  useFormContent,
  useFormError,
  useFormLoadingStatus,
  useFormSaveStatus,
  useFormSubmissionStatus,
  useMediaQuery,
} from '@hooks';
import { addSnack, resetSubmitFormState, submitFormRequest } from '@store';
import type { FormConfig, FormFieldProps, SecurityFormComponentProps, SecurityFormProps } from '@types';
import { validateValueEqual } from '@utils';

import { BlockRegionModal } from './subComponents';

export const SecurityForm = ({ onFormikInstance, formikRef }: SecurityFormComponentProps) => {
  const dispatch = useAppDispatch();
  const { id: application_id } = useParams();

  const initialSecurityValues: Omit<SecurityFormProps, 'search'> = {
    soft_request_limit: VALIDATION_LIMITS.REQUEST_LIMIT_DEFAULT,
    soft_request_limit_unit: 'qps',
    block_request_limit: VALIDATION_LIMITS.REQUEST_LIMIT_MAX,
    block_request_limit_unit: 'qps',
    block_action: 'javascript_verify',
    under_attack_mode: 0,
    user_agent_black_list: [],
    ip_black_list: [],
    country_black_list: [],
  };

  const formType = FormType.CdnSecurity;

  const data = useFormContent({ formType });

  const isDataLoading = useFormLoadingStatus({ formType });
  const isSubmitting = useFormSubmissionStatus({ formType });
  const submitError = useFormError({ formType });
  const saveSuccess = useFormSaveStatus(formType);

  const { t: tcButton } = useTranslation(Translation.Common, { keyPrefix: 'button' });
  const { t: tDescription } = useTranslation(Translation.Description, { keyPrefix: 'configuration.security' });
  const { t: tToasts } = useTranslation(Translation.Configuration, { keyPrefix: 'toasts.security' });
  const { t: tForm } = useTranslation(Translation.Configuration, { keyPrefix: 'forms.security' });

  const [openBlockRegionModal, setOpenBlockRegionModal] = useState<boolean>(false);
  const [showCurrentToast, setShowCurrentToast] = useState<boolean>(false);
  const [currentDataKey, setCurrentDataKey] = useState<string | null>(null);

  const isAboveSm = useMediaQuery(`(min-width: ${SCREEN_BREAKPOINTS.sm}px)`);

  const initialValues: SecurityFormProps = {
    soft_request_limit: data?.soft_request_limit ?? initialSecurityValues.soft_request_limit,
    soft_request_limit_unit: data?.soft_request_limit_unit ?? initialSecurityValues.soft_request_limit_unit,
    block_request_limit: data?.block_request_limit ?? initialSecurityValues.block_request_limit,
    block_request_limit_unit: data?.block_request_limit_unit ?? initialSecurityValues.block_request_limit_unit,
    block_action: data?.block_action ?? initialSecurityValues.block_action,
    under_attack_mode: data?.under_attack_mode ?? initialSecurityValues.under_attack_mode,
    user_agent_black_list: data?.user_agent_black_list ?? initialSecurityValues.user_agent_black_list,
    ip_black_list: data?.ip_black_list ?? initialSecurityValues.ip_black_list,
    country_black_list: data?.country_black_list ?? initialSecurityValues.country_black_list,
    search: '',
  };

  const handleSubmit = (values: SecurityFormProps) => {
    const { search, user_agent_black_list: user_agent, ip_black_list: ip, ...updatedValues } = values;
    const user_agent_black_list = user_agent?.map((value) => value.replace(/\n/g, '')) ?? [];
    const ip_black_list = ip?.map((value) => value.replace(/\n/g, '')) ?? [];
    dispatch(
      submitFormRequest({
        formType,
        formData: { application_id, user_agent_black_list, ip_black_list, ...updatedValues },
        globalOptions: {
          returnResult: true,
          response: true,
        },
      }),
    );
  };

  useEffect(() => {
    if (!submitError && !isSubmitting && saveSuccess) {
      if (showCurrentToast && currentDataKey) {
        dispatch(addSnack({ type: StatusType.Success, message: tToasts(`${currentDataKey}Configured`) }));
        setShowCurrentToast(false);
        setCurrentDataKey(null);
      }
    }
  }, [application_id, currentDataKey, isSubmitting, saveSuccess, showCurrentToast, submitError]);

  const renderFormField = (field: FormFieldProps, formikValues: SecurityFormProps) => {
    let isDisabled = isSubmitting;
    if (field.inactive) {
      const { field: watchField, condition } = field.inactive;
      const watchedValue = formikValues?.[watchField];
      isDisabled = condition(watchedValue) || isSubmitting;
    }
    const isSwitch = validateValueEqual(field.inputType, InputType.Switch);

    const { name, label, description, ...props } = field;

    return (
      <FieldCard key={name} title={field.title ?? label} description={description} besideTitle={isSwitch && !isAboveSm}>
        <FormField name={name} label={label} key={name} disabled={isDisabled} {...props} />
      </FieldCard>
    );
  };

  const onOptionClick = (event: string[], formik: FormikProps<SecurityFormProps>, name: keyof SecurityFormProps) => {
    if (!formik.errors[name]) {
      formik.setFieldValue(name, event);
      const cleanedArray = event.map((value) => value.replace(/\n/g, ''));
      dispatch(
        submitFormRequest({
          formType,
          formData: { application_id, [name]: cleanedArray },
          globalOptions: { returnResult: true, content: true, toast: false },
        }),
      );
      setShowCurrentToast(true);
      setCurrentDataKey(name);
    }
  };

  const SecurityFormInner = () => {
    useErrorFormInner({ formType, isSubmitting });
    useEffect(() => {
      if (formikRef?.current?.errors && Object.keys(formikRef.current.errors).length > 0) {
        formikRef.current.validateForm();
      }
    }, [tForm]);
    return null;
  };

  const handleOpenModal = useCallback((setFunction: React.Dispatch<React.SetStateAction<boolean>>) => {
    dispatch(resetSubmitFormState({ formType }));
    setFunction(() => true);
  }, []);

  if (isDataLoading) {
    return (
      <div className="space-y-4">
        {[...Array(7)].map((_, index) => (
          <Skeleton key={index} className="flex w-full rounded-2xl h-32" />
        ))}
      </div>
    );
  }

  const OpenSecurityModal = (config: FormConfig) => {
    const { openModal } = useCreateModal({
      formType,
      initialValues: {
        user_agent_black_list: initialValues.user_agent_black_list,
        ip_black_list: initialValues.ip_black_list,
      },
      formConfig: config,
      handleSubmit,
    });

    return openModal;
  };

  return (
    <Formik
      innerRef={formikRef}
      validationSchema={securityFormSchema}
      initialTouched={
        { soft_request_limit: true, block_request_limit: true } as Partial<Record<keyof SecurityFormProps, boolean>>
      }
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize={!data}
    >
      {(formik) => {
        if (onFormikInstance) onFormikInstance(formik);

        const openUserAgentModal = OpenSecurityModal(
          getUserAgentBlacklistFormConfig({
            value: formik.values.user_agent_black_list,
          }),
        );

        const openUserIpModal = OpenSecurityModal(
          getIpBlacklistFormConfig({
            value: formik.values.ip_black_list,
          }),
        );

        const handleModalResetAndClose = (
          modalSetter: React.Dispatch<React.SetStateAction<boolean>>,
          openModal: boolean,
          fieldName: keyof SecurityFormProps,
        ) => {
          setCurrentDataKey(null);
          formik.setFieldValue(fieldName, initialValues[fieldName]);
          formik.setFieldError(fieldName, undefined);
          modalSetter(!openModal);
        };

        const handleBlockRegionModalClose = () => {
          handleModalResetAndClose(setOpenBlockRegionModal, openBlockRegionModal, 'country_black_list');
        };

        const securityFields = getSecurityFormConfig({
          loading: isSubmitting,
          openUserAgentModal,
          openUserIpModal,
          openBlockRegionModal: () => handleOpenModal(setOpenBlockRegionModal),
        })?.inputList;

        return (
          <Fragment>
            <SecurityFormInner />
            {securityFields.map((field, index) => renderFormField(field, formik.values))}
            {openBlockRegionModal && (
              <BlockRegionModal
                open={openBlockRegionModal}
                label={{
                  title: tForm('configurationBlockRegion'),
                  description: tDescription('blockRegion_description'),
                  placeholder: tForm('searchByRegionName'),
                  confirmButton: tcButton('configure'),
                }}
                formik={formik}
                onSubmit={onOptionClick}
                onClose={handleBlockRegionModalClose}
                formType={formType}
                name="country_black_list"
              />
            )}
          </Fragment>
        );
      }}
    </Formik>
  );
};
